import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgIdleService } from '../services/idle.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-idle-popup',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './idle-popup.component.html',
    styleUrls: ['./idle-popup.component.scss']
})
export class IdlePopupComponent implements OnInit, OnDestroy {
    /**
     * Callback function to handle logout when session expires.
     * Should be provided by the host component (parent app).
     * 
     * Example:
     *   <app-idle-popup [onLogout]="handleLogout.bind(this)"></app-idle-popup>
     */
    @Input() onLogout: (() => void) | undefined;

    secondTimerLeft: string = '';
    isModalVisible: boolean = false;
    private secondLevelSubscription: Subscription;
    private modalElement: HTMLElement;

    constructor(
        private idleService: NgIdleService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.secondLevelSubscription = this.idleService.secondLevelUserIdleChecker.subscribe(
            (timerValue: string) => {
                if (timerValue === 'SECOND_TIMER_STARTED') {
                    this.secondTimerLeft = `${this.idleService.FINAL_LEVEL_TIMER_VALUE_IN_MIN - 1}:59`;
                    this.showModal();
                } else if (timerValue === 'SECOND_TIMER_STOPPED') {
                    this.hideModal();
                    this.logout();
                } else if (timerValue === 'INITIATE_SECOND_TIMER' || timerValue === 'RESET_TIMER' || timerValue === 'STOPPED_TIMER') {
                    this.hideModal();
                    this.secondTimerLeft = '';
                } else {
                    this.secondTimerLeft = timerValue;
                }
            }
        );
    }

    ngOnDestroy(): void {
        if (this.secondLevelSubscription) {
            this.secondLevelSubscription.unsubscribe();
        }
        this.hideModal();
    }

    showModal(): void {
        const currentUrl = this.router?.url || '';
        const isAuthRoute = currentUrl.includes('login') || currentUrl.includes('join') || currentUrl === '/';

        if (isAuthRoute || !this.idleService || this.idleService.USER_IDLE_TIMER_VALUE_IN_MIN <= 0 || this.idleService.FINAL_LEVEL_TIMER_VALUE_IN_MIN <= 0) {
            this.hideModal();
            return;
        }

        this.isModalVisible = true;
        this.modalElement = document.getElementById('idlePopupModal');
        if (this.modalElement) {
            this.modalElement.style.display = 'block';
        }
    }

    hideModal(): void {
        this.isModalVisible = false;
        this.secondTimerLeft = '';
        if (this.modalElement) {
            this.modalElement.style.display = 'none';
        }
    }

    continue(): void {
        console.log('IdlePopupComponent: User continued session, restarting timer');
        // Hide modal and restart timer (this will reset flags and reinitialize interval)
        this.hideModal();
        this.idleService.restartTimer();
    }

    logout(): void {
        this.hideModal();

        // Use the injected logout callback if provided
        if (this.onLogout) {
            try {
                this.onLogout();
            } catch (error) {
                console.error('IdlePopupComponent: Error in logout callback', error);
                this.fallbackLogout();
            }
        } else {
            console.warn('IdlePopupComponent: No logout callback provided, using fallback');
            this.fallbackLogout();
        }
    }

    private fallbackLogout(): void {
        // Simple fallback: reload the page to login screen
        window.location.href = '/';
    }
}
